import * as React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { ConfirmDialog, EmptyState, NumberField, SectionCard, currency } from './ui';
import { TRUE_COLORS } from '../theme';

export type ModifierOption = { id?: number; name: string; price_delta: number | string; is_default: boolean; enabled?: boolean };
export type ModifierGroup = {
  id: number; name: string; min_select: number; max_select: number | null; enabled: boolean;
  modifiers: ModifierOption[]; products: number[];
};
type Draft = Omit<ModifierGroup, 'id'> & { id?: number };
type ProductRef = { id: number; name: string };

type Rule = 'one-required' | 'one-optional' | 'many';
const ruleOf = (g: Pick<ModifierGroup, 'min_select' | 'max_select'>): Rule =>
  g.max_select === 1 ? (g.min_select >= 1 ? 'one-required' : 'one-optional') : 'many';

/** How the register asks for the group, in words. */
export const ruleLabel = (g: Pick<ModifierGroup, 'min_select' | 'max_select'>) => {
  if (g.max_select === 1) return g.min_select >= 1 ? 'Elige 1 · obligatorio' : 'Elige 1 · opcional';
  const max = g.max_select == null ? 'las que quiera' : `hasta ${g.max_select}`;
  return g.min_select > 0 ? `Elige ${g.min_select} o más, ${max}` : `Elige ${max}`;
};

const priceLabel = (delta: number | string) => (Number(delta) ? `+${currency(delta)}` : '');

const emptyDraft = (): Draft => ({ name: '', min_select: 1, max_select: 1, enabled: true, products: [], modifiers: [{ name: '', price_delta: 0, is_default: false }] });

const errorText = (err: any) => {
  const data = err?.response?.data;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return 'No se pudo guardar.';
};

const GroupDialog = ({ open, initial, products, onClose, onSave }: {
  open: boolean; initial: Draft | null; products: ProductRef[]; onClose: () => void; onSave: (draft: Draft) => Promise<void>;
}) => {
  const [draft, setDraft] = React.useState<Draft>(emptyDraft());
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (open) { setDraft(initial ?? emptyDraft()); setError(''); } }, [open, initial]);

  const rule = ruleOf(draft);
  const setRule = (value: Rule) => setDraft(d => ({
    ...d,
    ...(value === 'one-required' ? { min_select: 1, max_select: 1 } : value === 'one-optional' ? { min_select: 0, max_select: 1 } : { min_select: 0, max_select: null }),
    // A single choice keeps at most one default.
    modifiers: value === 'many' ? d.modifiers : d.modifiers.map((m, i) => ({ ...m, is_default: m.is_default && d.modifiers.findIndex(x => x.is_default) === i })),
  }));
  const setOption = (index: number, patch: Partial<ModifierOption>) => setDraft(d => ({
    ...d,
    modifiers: d.modifiers.map((m, i) => {
      if (i === index) return { ...m, ...patch };
      // Choosing a default in a single-choice group clears the previous one.
      return patch.is_default && d.max_select === 1 ? { ...m, is_default: false } : m;
    }),
  }));
  const move = (index: number, delta: number) => setDraft(d => {
    const modifiers = [...d.modifiers];
    const [m] = modifiers.splice(index, 1);
    modifiers.splice(index + delta, 0, m);
    return { ...d, modifiers };
  });

  const save = async () => {
    setSaving(true); setError('');
    try {
      await onSave({ ...draft, modifiers: draft.modifiers.filter(m => m.name.trim()) });
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedProducts = products.filter(p => draft.products.includes(p.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{draft.id ? 'Editar grupo de opciones' : 'Nuevo grupo de opciones'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre" required autoFocus value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            helperText="Así lo verán el cajero y tus clientes, p. ej. Tamaño, Color o Extras." />
          <TextField select label="Cómo se elige" value={rule} onChange={e => setRule(e.target.value as Rule)}>
            <MenuItem value="one-required">Elige una, obligatorio</MenuItem>
            <MenuItem value="one-optional">Elige una, opcional</MenuItem>
            <MenuItem value="many">Elige varias</MenuItem>
          </TextField>
          {rule === 'many' && (
            <Stack direction="row" spacing={2}>
              <NumberField label="Mínimo" value={draft.min_select} decimals={false} sx={{ flex: 1 }}
                onChange={v => setDraft(d => ({ ...d, min_select: v ?? 0 }))}
                helperText="0 = opcional" />
              <NumberField label="Máximo" value={draft.max_select} decimals={false} sx={{ flex: 1 }}
                onChange={v => setDraft(d => ({ ...d, max_select: v ? v : null }))}
                helperText="Vacío = sin límite" />
            </Stack>
          )}

          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1 }}>Opciones</Typography>
            <Stack spacing={1}>
              {draft.modifiers.map((m, i) => (
                <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <TextField size="small" placeholder="Nombre de la opción" value={m.name} sx={{ flex: 2 }}
                    onChange={e => setOption(i, { name: e.target.value })} />
                  <TextField size="small" type="number" value={m.price_delta} sx={{ flex: 1, minWidth: 96 }}
                    onChange={e => setOption(i, { price_delta: e.target.value })}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start">+$</InputAdornment> }, htmlInput: { step: '0.5' } }} />
                  <Tooltip title="Ya viene elegida al vender">
                    <FormControlLabel sx={{ m: 0 }} label={<Typography variant="body2">Predet.</Typography>}
                      control={<Checkbox size="small" checked={m.is_default} onChange={e => setOption(i, { is_default: e.target.checked })} />} />
                  </Tooltip>
                  <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Subir"><ArrowUpwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" disabled={i === draft.modifiers.length - 1} onClick={() => move(i, 1)} aria-label="Bajar"><ArrowDownwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => setDraft(d => ({ ...d, modifiers: d.modifiers.filter((_, j) => j !== i) }))} aria-label="Quitar opción">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
              onClick={() => setDraft(d => ({ ...d, modifiers: [...d.modifiers, { name: '', price_delta: 0, is_default: false }] }))}>
              Agregar opción
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}>
              El precio se suma al del producto. Déjalo en 0 si no cambia el precio.
            </Typography>
          </Box>

          <Autocomplete
            multiple
            options={products}
            value={selectedProducts}
            getOptionLabel={p => p.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, value) => setDraft(d => ({ ...d, products: value.map(p => p.id) }))}
            renderTags={(value, getTagProps) => value.map((p, i) => <Chip size="small" label={p.name} {...getTagProps({ index: i })} key={p.id} />)}
            renderInput={params => <TextField {...params} label="Productos que la ofrecen" placeholder={selectedProducts.length ? '' : 'Elige productos'} />}
            noOptionsText="Sin productos"
          />
          <FormControlLabel
            control={<Checkbox checked={draft.enabled} onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))} />}
            label={<Typography variant="body2">Activo (se pregunta al vender y aparece en tu catálogo en línea)</Typography>}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || !draft.name.trim()}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** /admin/modifiers: sizes, milks, extras… chosen when selling a product and shown on the menu. */
const ModifierGroups = () => {
  const axios = useAxios();
  const [groups, setGroups] = React.useState<ModifierGroup[] | null>(null);
  const [products, setProducts] = React.useState<ProductRef[]>([]);
  const [editing, setEditing] = React.useState<Draft | null>(null);
  const [removing, setRemoving] = React.useState<ModifierGroup | null>(null);
  const [refresh, setRefresh] = React.useState(0);

  React.useEffect(() => {
    axios.get('api/modifier-groups/').then(r => setGroups(r.data ?? [])).catch(() => setGroups([]));
  }, [axios, refresh]);
  React.useEffect(() => {
    axios.get('api/products/').then(r => setProducts((r.data ?? []).map((p: any) => ({ id: p.id, name: p.name })))).catch(() => {});
  }, [axios]);

  const productName = (id: number) => products.find(p => p.id === id)?.name;
  const save = async (draft: Draft) => {
    const payload = { ...draft, modifiers: draft.modifiers.map(m => ({ ...m, price_delta: Number(m.price_delta) || 0 })) };
    if (draft.id) await axios.put(`api/modifier-groups/${draft.id}/`, payload);
    else await axios.post('api/modifier-groups/', payload);
    setRefresh(r => r + 1);
  };

  return (
    <PageShell
      back={{ to: '/admin/products', label: 'Productos' }}
      title="Opciones"
      subtitle="Variantes y complementos que el cajero elige al vender. También aparecen en tu catálogo en línea."
      actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(emptyDraft())}>Nuevo grupo</Button>}
    >
      {groups === null ? <Skeleton variant="rounded" height={240} /> : groups.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<TuneOutlinedIcon />}
            title="Agrega tus primeras opciones"
            description="Crea un grupo una vez y elige los productos que lo ofrecen; cada opción puede sumar al precio. Por ejemplo, «Tamaño»: Chico, Grande +$10."
            action={<Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEditing(emptyDraft())}>Crear grupo</Button>}
          />
        </SectionCard>
      ) : (
        <Grid container spacing={3}>
          {groups.map(group => (
            <Grid key={group.id} size={{ xs: 12, md: 6 }}>
              <SectionCard
                title={<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} component="span">
                  <span>{group.name}</span>
                  {!group.enabled && <Chip size="small" variant="outlined" label="Inactivo" />}
                </Stack>}
                subtitle={ruleLabel(group)}
                action={
                  <Stack direction="row">
                    <Tooltip title="Editar"><IconButton onClick={() => setEditing({ ...group })}><EditOutlinedIcon /></IconButton></Tooltip>
                    <Tooltip title="Eliminar"><IconButton onClick={() => setRemoving(group)}><DeleteOutlineIcon sx={{ color: TRUE_COLORS.danger }} /></IconButton></Tooltip>
                  </Stack>
                }
              >
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {group.modifiers.map(m => (
                    <Chip key={m.id} size="small" variant={m.is_default ? 'filled' : 'outlined'}
                      label={`${m.name}${priceLabel(m.price_delta) ? ` ${priceLabel(m.price_delta)}` : ''}${m.is_default ? ' · predet.' : ''}`} />
                  ))}
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                  {group.products.length === 0 ? 'Ningún producto la ofrece todavía.'
                    : `En ${group.products.length} producto${group.products.length === 1 ? '' : 's'}: ${group.products.map(productName).filter(Boolean).slice(0, 4).join(', ')}${group.products.length > 4 ? '…' : ''}`}
                </Typography>
              </SectionCard>
            </Grid>
          ))}
        </Grid>
      )}

      <GroupDialog open={Boolean(editing)} initial={editing} products={products} onClose={() => setEditing(null)} onSave={save} />
      <ConfirmDialog
        open={Boolean(removing)}
        danger
        title={`¿Eliminar "${removing?.name}"?`}
        description="Los productos dejarán de ofrecer estas opciones. Las ventas pasadas conservan lo que se eligió."
        confirmLabel="Eliminar"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (removing) await axios.delete(`api/modifier-groups/${removing.id}/`);
          setRemoving(null);
          setRefresh(r => r + 1);
        }}
      />
    </PageShell>
  );
};

export default ModifierGroups;
